import type { AppListing } from '@/backend/types';

/**
 * The apps a new person sees before they have built anything.
 *
 * These are not mock-ups and not a separate code path: each one was generated by Saddle
 * from a sentence, gated and published like anyone else's, and each is live at the URL
 * below. Opening one opens exactly what a person's own app becomes — which is the whole
 * claim the product makes, so demonstrating it with anything else would be a lie told at
 * the first moment it could be told.
 *
 * They replace two demos built for the previous product: apps the on-device engine ran
 * from a JSON definition, one of them wired to HealthKit. Both still worked, and both
 * showed a Saddle that no longer exists.
 *
 * The copy here is written rather than taken from the catalog: an app's stored description
 * is the prompt it was built from, truncated mid-word, which is fine for a row in a list
 * of your own apps and not fine as the first sentence anyone reads about Saddle.
 *
 * Rebuilding the set: `E2E_DEMOS=1 npx jest demos.live` publishes them and writes
 * /tmp/saddle-demos/demos.json with the URLs. If a slug changes, change it here too.
 */

const CREATOR = 'saddle-demos';
const PUBLISHED_AT = '2026-09-09T00:00:00.000Z';

interface WebDemo {
  /**
   * The app's REAL id in the catalog — deliberately not a `demo:` id.
   *
   * That prefix is reserved for apps the on-device engine runs out of local SQLite, and
   * anything wearing it gets routed there. These are published web apps: a real id means
   * every path that looks one up gets a real answer instead of a spinner.
   */
  appId: string;
  slug: string;
  name: string;
  /** One sentence, in the voice of the person who would have made it. */
  description: string;
  icon: string;
  category: string;
  kit: string;
  /** What this one is here to show, for the card's second line. */
  proves: string;
}

const DEMOS: WebDemo[] = [
  {
    appId: 'bmlqDWbr6aLUM1WFRPQ-D',
    slug: 'pottery-class-bookings-2',
    name: 'Pottery classes',
    description: 'A timetable of classes people book a place on. A full session says so instead of taking another booking.',
    icon: 'color-palette',
    category: 'Bookings',
    kit: 'ledger',
    proves: 'Places held by the server, not by the page',
  },
  {
    appId: 'mg3piEmbXoA2wgPbqVhYx',
    slug: 'cafe-rota',
    name: 'Cafe rota',
    description: "The week's shifts, posted by the manager and claimed by staff. Tuesday can go up on Monday night.",
    icon: 'cafe',
    category: 'Work',
    kit: 'console',
    proves: 'Content the owner changes without rebuilding',
  },
  {
    appId: 'mbOKm01Hmjcxm5DG5ny1W',
    slug: 'food-bank-shift-board-4',
    name: 'Volunteer shifts',
    description: 'A shift board for a food bank. Volunteers claim a slot; a full shift stops taking people and past ones grey out.',
    icon: 'people',
    category: 'Community',
    kit: 'ledger',
    proves: 'A limit that refuses the next claim',
  },
  {
    appId: 'XBKo8nwRJa87nvb84q1Z9',
    slug: 'small-batch-coffee-2',
    name: 'Coffee waiting list',
    description: 'A launch page with a real waiting list. You confirm by email, and the page invents no numbers.',
    icon: 'mail',
    category: 'Launch',
    kit: 'soft',
    proves: 'A double opt-in list that actually mails',
  },
  {
    appId: 'RdNtsDC9TIAAKQSf0wIu6',
    slug: 'our-wedding-weekend-3',
    name: 'Wedding weekend',
    description: 'Three events across a weekend. Guests sign in, say what they are coming to, and can change their answer later.',
    icon: 'heart',
    category: 'Events',
    kit: 'editorial',
    proves: 'Accounts — an answer that follows you between devices',
  },
  {
    appId: 'hX98MEiGaQEa1Q31L6tmH',
    slug: 'ammiya-2',
    name: 'Everyday Arabic',
    description: 'A deck that writes its own cards, reviews you on them, and brings back whatever you keep missing.',
    icon: 'school',
    category: 'Learning',
    kit: 'sport',
    proves: 'AI making the content, and right-to-left text',
  },
];

/** Where a bundled demo is published. Absolute, because it is opened in a browser. */
export function demoSiteUrl(slug: string): string {
  return `https://app.saddle-sites.com/site/${slug}`;
}

/**
 * A demo as a listing.
 *
 * `webPublished` is what tells every surface to open the URL rather than hand the app to
 * the on-device engine, which has no idea what these are. `remixOnly` is what puts the
 * DEMO badge on the card.
 */
export function webDemoListings(): AppListing[] {
  return DEMOS.map((d) => ({
    appId: d.appId,
    name: d.name,
    description: d.description,
    icon: d.icon,
    category: d.category,
    creatorId: CREATOR,
    publishedAt: PUBLISHED_AT,
    remixOnly: true,
    webPublished: true,
    webVisibility: 'public' as const,
    slug: d.slug,
    kit: d.kit,
    siteUrl: demoSiteUrl(d.slug),
  }));
}

/** What a demo is in the set to show, for a card that has room to say. */
export function demoProves(appId: string): string | undefined {
  return DEMOS.find((d) => d.appId === appId)?.proves;
}

/** True for a bundled demo, so a surface can badge it without hardcoding ids. */
export function isWebDemo(appId: string | undefined | null): boolean {
  return !!appId && DEMOS.some((d) => d.appId === appId);
}

/**
 * The demos as the SERVER has them, falling back to the copy above.
 *
 * The list shipped in the binary, so rebuilding a demo — which can hand it a new slug —
 * broke every installed app's My Apps until the next App Store review. The server is the
 * one that may change; this copy exists for a first run with no network, where showing
 * six apps that might be slightly stale beats showing none.
 */
export async function fetchWebDemoListings(baseUrl: string): Promise<AppListing[]> {
  try {
    const res = await fetch(`${baseUrl.replace(/\/+$/, '')}/demos`);
    if (!res.ok) return webDemoListings();
    const body = (await res.json()) as { items?: AppListing[] };
    // An empty answer is a bad answer: a new person with no demos has nothing to open.
    return body.items?.length ? body.items : webDemoListings();
  } catch {
    return webDemoListings();
  }
}
